import React, { useEffect, useRef, useCallback } from "react";
import { Tooltip } from "@material-ui/core";
import { ScreenshotPotree } from "components/screenshot-potree/ScreenshotPotree";
import "./potree.css";

const DEFAULT_POINT_BUDGET = 1_000_000;

function PotreePointcloud({
  metadataUrl,
  folderPath,
  compareMode = false,
  compareMetadataUrlA,
  compareMetadataUrlB,
  compareView = "toggle",
  activeCompareDate = "A",
  onViewerReady,
  canCompare = false,
  onToggleCompare
}) {
  const renderAreaRef = useRef(null);
  const viewerRef = useRef(null);
  const pointCloudARef = useRef(null);
  const pointCloudBRef = useRef(null);

  const COMPASS_ROSE_SVG_DATA_URL = (() => {
    // Ícone propositalmente "preto" para o filter CSS do Potree (brightness/ invert / hover)
    // conseguir padronizar para o azul do design system.
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">` +
      `<circle cx="12" cy="12" r="9" />` +
      `<path d="M12 3 L14.2 10.0 L21 12 L14.2 14.0 L12 21 L9.8 14.0 L3 12 L9.8 10.0 Z" />` +
      `<path d="M12 8.2 L13.2 12 L12 15.8 L10.8 12 Z" />` +
      `</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  })();

  const TRASH_SVG_DATA_URL = (() => {
    const svg =
      `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="black" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">` +
      `<polyline points="3 6 5 6 21 6" />` +
      `<path d="M19 6l-1 14H6L5 6" />` +
      `<path d="M10 11v6" />` +
      `<path d="M14 11v6" />` +
      `<path d="M9 6V4h6v2" />` +
      `</svg>`;
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  })();

  const normalizeToolText = text =>
    String(text || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const removePointCloudCompat = (scene, pointcloud) => {
    if (!scene || !pointcloud) return;
    if (typeof scene.removePointCloud === "function") {
      scene.removePointCloud(pointcloud);
      return;
    }
    // Fallback para versões em que o método não existe:
    // `scene` é um THREE.Scene e aceita `remove(obj)`
    if (typeof scene.remove === "function") {
      scene.remove(pointcloud);
      return;
    }
  };

  // Cleanup helper
  const disposeViewer = useCallback(() => {
    if (viewerRef.current) {
      const scene = viewerRef.current.scene;
      const pcs = scene?.pointclouds || [];
      while (pcs.length > 0) {
        removePointCloudCompat(scene, pcs[0]);
      }
      viewerRef.current.renderer?.dispose();
      viewerRef.current.renderer?.forceContextLoss();
      const el = document.getElementById("potree_render_area");
      if (el) el.innerHTML = "";
      viewerRef.current = null;
    }
    pointCloudARef.current = null;
    pointCloudBRef.current = null;
  }, []);

  // Inicializar viewer
  useEffect(() => {
    const el = document.getElementById("potree_render_area");
    if (!el) return;

    const viewer = new window.Potree.Viewer(el);
    viewerRef.current = viewer;

    viewer.setEDLEnabled(false);
    viewer.setFOV(60);
    viewer.setPointBudget(DEFAULT_POINT_BUDGET);
    viewer.loadSettingsFromURL();
    viewer.setBackground("skybox");
    viewer.loadGUI(() => {
      viewer.setLanguage("br");
      window
        .$("#menu_tools")
        .next()
        .show();
      window
        .$("#menu_clipping")
        .next()
        .show();
      viewer.toggleSidebar();

      requestAnimationFrame(() => {
        const icons = Array.from(document.querySelectorAll("#tools img.button-icon"));

        // Alguns ícones do Potree não vêm com `img.title` definido.
        // Ajuste específico: "Orientação (Azimute)" fica logo abaixo da medição de círculo.
        const getTooltip = img => {
          const closestLi = img?.closest?.("li");
          return (
            img?.title ||
            img?.getAttribute?.("aria-label") ||
            img?.getAttribute?.("data-tooltip") ||
            img?.parentElement?.title ||
            closestLi?.title ||
            ""
          );
        };

        const isCircleTool = tooltip =>
          /círculo|circulo|circle/i.test(String(tooltip || ""));

        const circleIdx = icons.findIndex(img => isCircleTool(getTooltip(img)));
        if (circleIdx !== -1 && icons[circleIdx + 1]) {
          const next = icons[circleIdx + 1];
          const nextTooltip = getTooltip(next);
          if (!nextTooltip) {
            // Preferimos setar no próprio img para o wrapper abaixo reaproveitar `img.title`.
            next.title = "Orientação (Azimute)";
            if (next.parentElement) next.parentElement.title = "Orientação (Azimute)";
            const li = next.closest?.("li");
            if (li) li.title = "Orientação (Azimute)";
          }

          // Troca visual (sempre que for a ferramenta logo abaixo da "medição de círculo").
          // Mantém consistência com a estética dos demais ícones (via filter CSS).
          const effectiveTooltip = getTooltip(next);
          const isAzimuteTool =
            !nextTooltip || /Azimute/i.test(String(effectiveTooltip || "")) || /orienta/i.test(String(effectiveTooltip || ""));
          if (isAzimuteTool && next && typeof next.src === "string") {
            next.src = COMPASS_ROSE_SVG_DATA_URL;
          }
        }

        icons.forEach(img => {
          if (img.parentElement?.classList?.contains("v4d-tool-wrap")) return;
          const wrap = document.createElement("div");
          wrap.className = "v4d-tool-wrap";
          const tooltip = getTooltip(img);
          if (tooltip) wrap.setAttribute("data-tooltip", tooltip);
          img.parentNode.insertBefore(wrap, img);
          wrap.appendChild(img);
        });

        // Ajustes visuais extras em tools do Potree (troca de ícones por SVG)
        icons.forEach(img => {
          const tooltip = getTooltip(img);
          const n = normalizeToolText(tooltip);

          // Remover todas as medições / Limpar medições
          if (/remover|limpar/.test(n) && /medic/.test(n)) {
            if (img && typeof img.src === "string") img.src = TRASH_SVG_DATA_URL;
          }
        });
      });
    });

    if (onViewerReady) onViewerReady(viewer);

    return disposeViewer;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carregar point cloud normal (modo nao-compare)
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || compareMode || !metadataUrl) return;

    // Limpar point clouds existentes
    const pcs = viewer.scene?.pointclouds || [];
    while (pcs.length > 0) {
      removePointCloudCompat(viewer.scene, pcs[0]);
    }
    pointCloudARef.current = null;
    pointCloudBRef.current = null;
    viewer.setPointBudget(DEFAULT_POINT_BUDGET);

    fetch(metadataUrl)
      .then(response => {
        if (!response.ok) throw new Error(`Erro HTTP! Status: ${response.status}`);
        return response.json();
      })
      .then(metadata => {
        viewer.setDescription(metadata.name);
        window.Potree.loadPointCloud(metadataUrl, metadata.name, e => {
          const pointcloud = e.pointcloud;
          pointcloud.material.size = 1;
          pointcloud.material.pointSizeType = window.Potree.PointSizeType.ADAPTIVE;
          pointcloud.material.shape = window.Potree.PointShape.SQUARE;
          viewer.scene.addPointCloud(pointcloud);
          viewer.fitToScreen();
        });
      })
      .catch(err => {
        console.error("Falha ao carregar metadata do ponto de nuvem:", err);
      });
  }, [metadataUrl, compareMode]);

  // Carregar duas point clouds no modo compare
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer || !compareMode || !compareMetadataUrlA || !compareMetadataUrlB) return;

    // Limpar point clouds existentes
    const pcs = viewer.scene?.pointclouds || [];
    while (pcs.length > 0) {
      removePointCloudCompat(viewer.scene, pcs[0]);
    }
    pointCloudARef.current = null;
    pointCloudBRef.current = null;

    const loadPC = (url, ref) =>
      new Promise((resolve, reject) => {
        fetch(url)
          .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
          .then(meta => {
            window.Potree.loadPointCloud(url, meta.name, e => {
              const pc = e.pointcloud;
              pc.material.size = 1;
              pc.material.pointSizeType = window.Potree.PointSizeType.ADAPTIVE;
              pc.material.shape = window.Potree.PointShape.SQUARE;
              ref.current = pc;
              viewer.scene.addPointCloud(pc);
              resolve(pc);
            });
          })
          .catch(reject);
      });

    Promise.all([
      loadPC(compareMetadataUrlA, pointCloudARef),
      loadPC(compareMetadataUrlB, pointCloudBRef)
    ])
      .then(() => viewer.fitToScreen())
      .catch(err => console.error("[Potree Compare] Erro ao carregar point clouds:", err));
  }, [compareMode, compareMetadataUrlA, compareMetadataUrlB]);

  // Toggle / Overlay visibility
  useEffect(() => {
    const pcA = pointCloudARef.current;
    const pcB = pointCloudBRef.current;
    const viewer = viewerRef.current;
    if (!pcA || !pcB || !viewer || !compareMode) return;

    if (compareView === "toggle") {
      viewer.setPointBudget(DEFAULT_POINT_BUDGET);
      pcA.visible = activeCompareDate === "A";
      pcB.visible = activeCompareDate === "B";
    } else {
      // overlay — ambas visiveis com budget dividido
      viewer.setPointBudget(500_000);
      pcA.visible = true;
      pcB.visible = true;
      // Diferenciar visualmente: B com tint azulado
      pcB.material.rgbBrightness = 0.15;
      pcB.material.rgbContrast = 0.1;
    }
  }, [compareMode, compareView, activeCompareDate]);

  return (
    <div
      className="potree-wrapper"
      style={{
        position: "absolute",
        top: "0",
        bottom: "0",
        left: "0",
        right: "0",
        zIndex: "10",
        width: "100%",
        height: "100%"
      }}
    >
      <div
        ref={renderAreaRef}
        id="potree_render_area"
        style={{
          position: "relative",
          top: "0",
          bottom: "0",
          left: "0",
          right: "0",
          width: "100%",
          height: "100%",
          overflow: "hidden"
        }}
      ></div>

      {/* SIDEBAR */}
      <div id="potree_sidebar_container"></div>

      {folderPath && !compareMode && (
        <ScreenshotPotree
          tagReference={renderAreaRef}
          viewerRef={viewerRef}
          folderPath={folderPath}
          compareButton={
            onToggleCompare ? (
              <Tooltip title={canCompare ? "Comparar datas" : "Comparação indisponível (necessário 2+ datas)"}>
                <span>
                  <button
                    type="button"
                    className="screenshot-toolbar__btn"
                    disabled={!canCompare}
                    onClick={onToggleCompare}
                    aria-label="Comparar datas"
                  >
                    <i className="fas fa-columns" />
                  </button>
                </span>
              </Tooltip>
            ) : null
          }
        />
      )}

      {/* No compare mode: so o botao comparar na toolbar */}
      {folderPath && compareMode && onToggleCompare && (
        <div className="screenshot-potree screenshot-toolbar">
          <Tooltip title="Sair da comparação">
            <button
              type="button"
              className="screenshot-toolbar__btn screenshot-toolbar__btn--active"
              onClick={onToggleCompare}
              aria-label="Sair da comparação"
            >
              <i className="fas fa-columns" />
            </button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

export default PotreePointcloud;
